import api from "./api";

// ========== Types ==========

export interface FormFileItem {
  path: string;
  name: string;
  size?: number;
}

export interface SubmittedFileItem {
  path: string;
  name: string;
  size?: number;
  mime_type?: string;
}

export interface RequiredDocumentTemplate {
  id: number;
  name: string;
  description?: string;
  document_type_code: string;
  is_mandatory: boolean;
  deadline_days: number;
  effective_date: string;
  sort_order: number;
  is_active: boolean;
  is_custom: boolean;
  allow_multiple_files: boolean;
  user_types?: string[];
  form_file_name?: string;
  form_files: FormFileItem[];
  created_by?: string;
  creator_name?: string;
  created_at: string;
  updated_at: string;
}

export interface TemplateCreateRequest {
  name: string;
  description?: string;
  document_type_code: string;
  is_mandatory: boolean;
  deadline_days: number;
  effective_date: string;
  sort_order?: number;
}

export interface TemplateUpdateRequest {
  name?: string;
  description?: string;
  document_type_code?: string;
  is_mandatory?: boolean;
  deadline_days?: number;
  effective_date?: string;
  sort_order?: number;
  is_active?: boolean;
}

export interface BulkTemplateItem {
  document_type_code: string;
  document_type_name: string;
  user_types: string[];
  is_mandatory: boolean;
  deadline_days: number;
  effective_date: string;
  allow_multiple_files: boolean;
  /** false면 비활성(미사용) 상태로 저장 — 양식 선등록용 템플릿 선생성 (기본 true) */
  is_active?: boolean;
}

export interface RequiredDocumentSubmission {
  id: number;
  template_id: number;
  template_name: string;
  document_type_code: string;
  is_mandatory: boolean;
  allow_multiple_files: boolean;
  user_id: string;
  user_name?: string;
  department_name?: string;
  status: string; // pending | submitted | approved | rejected | exemption_requested | exempted
  file_name?: string;
  file_size?: number;
  files: SubmittedFileItem[];
  submitted_at?: string;
  reviewed_by?: string;
  reviewer_name?: string;
  reviewed_at?: string;
  reject_reason?: string;
  exempt_reason?: string;
  exempt_type?: "user_request" | "admin_direct";
  form_file_name?: string;
  form_files: FormFileItem[];
  deadline_date?: string;
  created_at: string;
  // 제외(soft delete) — 관리자 화면에서만 노출
  is_excluded?: boolean;
  exclude_reason?: string;
  excluded_at?: string;
  // 인사기록을 근거로 자동 복원된 건이면 "employee_document:{id}"
  restored_from?: string;
}

export interface MandatoryCheckResult {
  has_pending_mandatory: boolean;
  pending_count: number;
  mandatory_count: number;
  skip_duration_minutes: number;
  is_past_deadline: boolean;
  has_active_extension: boolean;
  extension_expires_at?: string;
}

export interface ExtensionRequest {
  reason: string;
}

export interface ExtensionResponse {
  id: number;
  reason: string;
  expires_at: string;
  created_at: string;
}

export interface AdminExtension {
  id: number;
  user_id: string;
  user_name?: string;
  department_name?: string;
  reason: string;
  expires_at: string;
  created_at: string;
  is_active: boolean;
}

export interface RequiredDocSettings {
  skip_duration_minutes: number;
}

// ========== Service ==========

const requiredDocumentService = {
  // Admin: Templates
  // companyId: (super_admin 전용) 다른 기업의 설정 관리 시 대상 기업 ID
  getTemplates: async (activeOnly = false, companyId?: string): Promise<RequiredDocumentTemplate[]> => {
    const response = await api.get<RequiredDocumentTemplate[]>(
      "/required-documents/admin/templates",
      { params: { active_only: activeOnly, company_id: companyId } }
    );
    return response.data;
  },

  createTemplate: async (data: TemplateCreateRequest): Promise<RequiredDocumentTemplate> => {
    const response = await api.post<RequiredDocumentTemplate>(
      "/required-documents/admin/templates",
      data
    );
    return response.data;
  },

  updateTemplate: async (
    id: number,
    data: TemplateUpdateRequest
  ): Promise<RequiredDocumentTemplate> => {
    const response = await api.put<RequiredDocumentTemplate>(
      `/required-documents/admin/templates/${id}`,
      data
    );
    return response.data;
  },

  deleteTemplate: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/required-documents/admin/templates/${id}`);
    return response.data;
  },

  bulkSyncTemplates: async (
    items: BulkTemplateItem[],
    companyId?: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.put("/required-documents/admin/templates/bulk-sync", items, {
      params: { company_id: companyId },
    });
    return response.data;
  },

  loadDefaultForms: async (companyId?: string): Promise<{
    success: boolean;
    imported: { code: string; name: string }[];
    skipped: { code: string; name: string }[];
    message: string;
  }> => {
    const response = await api.post(
      "/required-documents/admin/templates/load-default-forms",
      undefined,
      { params: { company_id: companyId } }
    );
    return response.data;
  },

  // Admin: Submissions
  getSubmissions: async (params?: {
    template_id?: number;
    user_id?: string;
    status?: string;
    include_excluded?: boolean;
  }): Promise<RequiredDocumentSubmission[]> => {
    const response = await api.get<RequiredDocumentSubmission[]>(
      "/required-documents/admin/submissions",
      { params }
    );
    return response.data;
  },

  approveSubmission: async (
    id: number
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/required-documents/admin/submissions/${id}/approve`);
    return response.data;
  },

  rejectSubmission: async (
    id: number,
    rejectReason: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/required-documents/admin/submissions/${id}/reject`,
      { reject_reason: rejectReason }
    );
    return response.data;
  },

  approveExemption: async (
    id: number
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/required-documents/admin/submissions/${id}/approve-exemption`
    );
    return response.data;
  },

  rejectExemption: async (
    id: number
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/required-documents/admin/submissions/${id}/reject-exemption`
    );
    return response.data;
  },

  cancelExemptSubmission: async (
    id: number
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/required-documents/admin/submissions/${id}/cancel-exempt`
    );
    return response.data;
  },

  adminExemptSubmission: async (
    id: number,
    exemptReason: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/required-documents/admin/submissions/${id}/admin-exempt`,
      { exempt_reason: exemptReason }
    );
    return response.data;
  },

  // 실제 삭제가 아니라 '제외' 처리 — 지우면 재로그인 때 되살아나기 때문
  deleteSubmission: async (
    id: number,
    reason?: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(
      `/required-documents/admin/submissions/${id}`,
      { params: reason ? { reason } : undefined }
    );
    return response.data;
  },

  restoreExcludedSubmission: async (
    id: number
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/required-documents/admin/submissions/${id}/restore-exclusion`
    );
    return response.data;
  },

  // 인사기록에 서류가 있는데 '미제출'로 남아 있는 건을 '승인'으로 복원
  reconcileSubmissions: async (): Promise<{
    success: boolean;
    message: string;
    restored: number;
    skipped: number;
    details: string[];
  }> => {
    const response = await api.post(
      `/required-documents/admin/submissions/reconcile`
    );
    return response.data;
  },

  deleteSubmissionsBulk: async (
    submissionIds: number[]
  ): Promise<{
    success: boolean;
    message: string;
    deleted: number;
    skipped?: number;
    deleted_ids?: number[];
  }> => {
    const response = await api.post(
      `/required-documents/admin/submissions/bulk-delete`,
      submissionIds
    );
    return response.data;
  },

  cleanupOldSubmissions: async (
    pendingOnly: boolean = true
  ): Promise<{ success: boolean; message: string; deleted: number }> => {
    const response = await api.post(
      `/required-documents/admin/submissions/cleanup`,
      null,
      { params: { pending_only: pendingOnly } }
    );
    return response.data;
  },

  // Employee
  getMyPending: async (): Promise<RequiredDocumentSubmission[]> => {
    const response = await api.get<RequiredDocumentSubmission[]>(
      "/required-documents/my/pending"
    );
    return response.data;
  },

  getMyAll: async (): Promise<RequiredDocumentSubmission[]> => {
    const response = await api.get<RequiredDocumentSubmission[]>(
      "/required-documents/my/all"
    );
    return response.data;
  },

  submitDocument: async (templateId: number, files: File[]): Promise<{ success: boolean; message: string }> => {
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    const response = await api.post(
      `/required-documents/my/${templateId}/submit`,
      formData,
      { headers: { "Content-Type": undefined } }
    );
    return response.data;
  },

  resubmitDocument: async (submissionId: number, files: File[]): Promise<{ success: boolean; message: string }> => {
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    const response = await api.post(
      `/required-documents/my/${submissionId}/resubmit`,
      formData,
      { headers: { "Content-Type": undefined } }
    );
    return response.data;
  },

  requestExemption: async (
    submissionId: number,
    exemptReason: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/required-documents/my/${submissionId}/request-exemption`,
      { exempt_reason: exemptReason }
    );
    return response.data;
  },

  cancelExemptionRequest: async (
    submissionId: number
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/required-documents/my/${submissionId}/cancel-exemption-request`
    );
    return response.data;
  },

  withdrawSubmission: async (
    submissionId: number
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/required-documents/my/${submissionId}/withdraw`
    );
    return response.data;
  },

  // Extension (연장 요청)
  requestExtension: async (reason: string): Promise<ExtensionResponse> => {
    const response = await api.post<ExtensionResponse>(
      "/required-documents/my/request-extension",
      { reason }
    );
    return response.data;
  },

  // Form file (양식 파일)
  uploadFormFile: async (
    templateId: number,
    file: File,
    companyId?: string
  ): Promise<{ success: boolean; message: string; form_file_name: string }> => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(
      `/required-documents/admin/templates/${templateId}/upload-form`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        params: { company_id: companyId },
      }
    );
    return response.data;
  },

  deleteFormFile: async (
    templateId: number,
    filePath?: string,
    companyId?: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/required-documents/admin/templates/${templateId}/form`, {
      params: { file_path: filePath, company_id: companyId },
    });
    return response.data;
  },

  getFormDownloadUrl: async (
    templateId: number,
    filePath?: string,
    companyId?: string
  ): Promise<{ download_url: string; file_name: string }> => {
    const response = await api.get(`/required-documents/templates/${templateId}/download-form`, {
      params: { file_path: filePath, company_id: companyId },
    });
    return response.data;
  },

  getSubmissionDownloadUrl: async (submissionId: number, filePath?: string): Promise<{ download_url: string; file_name: string }> => {
    const response = await api.get(`/required-documents/admin/submissions/${submissionId}/download`, {
      params: filePath ? { file_path: filePath } : undefined,
    });
    return response.data;
  },

  // System
  checkMandatory: async (): Promise<MandatoryCheckResult> => {
    const response = await api.get<MandatoryCheckResult>(
      "/required-documents/check-mandatory"
    );
    return response.data;
  },

  // Admin: Extensions
  getExtensions: async (): Promise<AdminExtension[]> => {
    const response = await api.get<AdminExtension[]>(
      "/required-documents/admin/extensions"
    );
    return response.data;
  },

  // Admin: Settings
  getSettings: async (): Promise<RequiredDocSettings> => {
    const response = await api.get<RequiredDocSettings>(
      "/required-documents/admin/settings"
    );
    return response.data;
  },

  updateSettings: async (data: RequiredDocSettings): Promise<RequiredDocSettings> => {
    const response = await api.put<RequiredDocSettings>(
      "/required-documents/admin/settings",
      data
    );
    return response.data;
  },
};

export default requiredDocumentService;
